import { Component, Input, OnInit, OnDestroy, forwardRef, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface SelectOption {
  value: any;
  label: string;
  disabled?: boolean;
}

export interface GenericSelectConfig {
  placeholder?: string;
  searchPlaceholder?: string;
  noEntriesFoundLabel?: string;
  allowSearch?: boolean;
  allowMultiple?: boolean;
  displayProperty?: string;
  valueProperty?: string;
  searchProperties?: string[];
}

@Component({
  selector: 'app-generic-select',
  templateUrl: './generic-select.component.html',
  styleUrls: ['./generic-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GenericSelectComponent),
      multi: true
    }
  ]
})
export class GenericSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() options: any[] = [];
  @Input() config: GenericSelectConfig = {};
  @Input() label: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() panelClass: string | string[] = '';
  @Input() disableOptionCentering: boolean = false;

  @Output() selectionChange = new EventEmitter<any>();
  @Output() searchChange = new EventEmitter<string>();

  formSearch: FormControl = new FormControl('');
  selectedDisplayNames: string[] = [];
  debouncedSearchTerm: string = '';
  
  private subscriptions: Subscription = new Subscription();
  private value: any = null;
  private onChange = (value: any) => {};
  private onTouched = () => {};

  private defaultConfig: GenericSelectConfig = {
    placeholder: 'Select...',
    searchPlaceholder: 'Search...',
    noEntriesFoundLabel: 'No entries found',
    allowSearch: true,
    allowMultiple: false,
    displayProperty: 'label',
    valueProperty: 'value',
    searchProperties: ['label'],
  };

  ngOnInit() {
    // input config overrides default config
    this.config = { ...this.defaultConfig, ...this.config };
    
    this.subscriptions.add(
      this.formSearch.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ).subscribe(searchValue => {
        this.debouncedSearchTerm = searchValue || '';
        this.searchChange.emit(searchValue);
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateSelectedDisplayNames();
  }

  // For ControlValueAccessor
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Handle selection changes
  onSelectionChange(selectedValue: any): void {
    this.value = selectedValue;
    this.updateSelectedDisplayNames();
    this.onChange(selectedValue);
    this.onTouched();
    this.selectionChange.emit(selectedValue);
  }

  // Get filtered options based on search
  getFilteredOptions(): any[] {
    if (!this.config.allowSearch || !this.debouncedSearchTerm) {
      return this.options;
    }

    const searchText = this.debouncedSearchTerm.toLowerCase();
    const searchProps = this.config.searchProperties!;

    return this.options.filter(option => {
      // Handle simple string/number options
      if (typeof option === 'string' || typeof option === 'number') {
        return option.toString().toLowerCase().includes(searchText);
      }
      
      // Handle object options
      return searchProps.some(prop => {
        const value = this.getNestedProperty(option, prop);
        return value && value.toString().toLowerCase().includes(searchText);
      });
    });
  }

  // Helper methods
  getOptionValue(option: any): any {
    // If option is a simple string/number, return it directly
    if (typeof option === 'string' || typeof option === 'number') {
      return option;
    }
    // If option is an object, use the configured value property
    return this.getNestedProperty(option, this.config.valueProperty!);
  }

  getOptionDisplay(option: any): string {
    // If option is a simple string/number, return it directly
    if (typeof option === 'string' || typeof option === 'number') {
      return option.toString();
    }
    // If option is an object, use the configured display property
    return this.getNestedProperty(option, this.config.displayProperty!);
  }

  private getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current && current[prop], obj);
  }

  // Update selected display names based on current value
  private updateSelectedDisplayNames(): void {
    this.selectedDisplayNames = [];
    
    if (!this.value) {
      return;
    }

    if (this.config.allowMultiple && Array.isArray(this.value)) {
      // Handle multi-select
      this.selectedDisplayNames = this.value.map(val => {
        const option = this.options.find(opt => this.getOptionValue(opt) === val);
        return option ? this.getOptionDisplay(option) : val.toString();
      });
    } else if (!this.config.allowMultiple) {
      // Handle single select
      const option = this.options.find(opt => this.getOptionValue(opt) === this.value);
      if (option) {
        this.selectedDisplayNames = [this.getOptionDisplay(option)];
      } else {
        this.selectedDisplayNames = [this.value.toString()];
      }
    }
  }
}